/**
 * Die Wettkampfregeln der drei Disziplinen.
 *
 * Jede Disziplin ist ein kleiner Zustandsautomat, der auf dem ScrollTracker
 * aufsitzt. Die Trennung ist Absicht: der Tracker misst, diese Klassen
 * bewerten. Neue Disziplinen kommen hier dazu, ohne die Messung anzufassen.
 */

import type { ScrollTracker, TrackerSample } from './scrollTracker'

export type Discipline = 'langstrecke' | 'sprung' | 'sprint'

export const DISCIPLINE_LABELS: Record<Discipline, string> = {
  langstrecke: 'Langstrecke',
  sprung: 'Sprung',
  sprint: 'Sprint',
}

/** Die ausgeschriebenen Sprint-Distanzen, in Zentimetern. */
export const SPRINT_DISTANCES = [
  { id: '100m', label: '100 m', cm: 10_000 },
  { id: '200m', label: '200 m', cm: 20_000 },
  { id: '400m', label: '400 m', cm: 40_000 },
  { id: '1km', label: '1 km', cm: 100_000 },
] as const

export type SprintDistanceId = (typeof SPRINT_DISTANCES)[number]['id']

/**
 * Anzeigename einer Variante.
 *
 * Die Kennung '100m' ist ein Schluessel fuer Datenbank und API, kein Text fuer
 * Menschen - in der Oberflaeche gehoert '100 m' hin, mit Leerzeichen vor der
 * Einheit. Diese Funktion ist die eine Stelle, die das uebersetzt.
 */
export function variantLabel(variant: string | null): string | null {
  if (!variant) return null
  return SPRINT_DISTANCES.find((entry) => entry.id === variant)?.label ?? variant
}

/** Ein abgeschlossener Versuch, so wie er in Bestenliste und API landet. */
export interface RunResult {
  discipline: Discipline
  /** Sprint-Distanz bzw. null bei Langstrecke und Sprung. */
  variant: string | null
  /** Zurueckgelegte Strecke in Zentimetern. */
  distanceCm: number
  /** Benoetigte Zeit in Millisekunden; bei Langstrecke die aktive Zeit. */
  timeMs: number
  /** Stuetzpunkte fuer die Plausibilitaetspruefung. */
  samples: TrackerSample[]
}

// ---------------------------------------------------------------------------
// Sprung
// ---------------------------------------------------------------------------

/**
 * 'bereit'    - wartet auf den Anlauf
 * 'anlauf'    - Finger liegt auf, der Wisch laeuft
 * 'flug'      - Finger ist weg, die Bahn rollt aus
 * 'gemessen'  - Ergebnis steht
 * 'uebertreten' - waehrend des Ausrollens erneut angefasst, Versuch ungueltig
 */
export type JumpPhase = 'bereit' | 'anlauf' | 'flug' | 'gemessen' | 'uebertreten'

/** Unterhalb dieser Strecke gilt der Versuch als nicht zustande gekommen. */
const MIN_JUMP_CM = 1

export class JumpAttempt {
  phase: JumpPhase = 'bereit'
  resultCm = 0
  /** Anteil der Strecke, der auf das Ausrollen entfiel - die "Flugphase". */
  flightShare = 0

  private tracker: ScrollTracker
  private onChange: () => void
  private cmAtRelease = 0

  constructor(tracker: ScrollTracker, onChange: () => void) {
    this.tracker = tracker
    this.onChange = onChange
    this.tracker.setEvents({
      onTouchStart: this.handleTouchStart,
      onTouchEnd: this.handleTouchEnd,
      onSettle: this.handleSettle,
    })
  }

  private handleTouchStart = (): void => {
    if (this.phase === 'flug') {
      // Anfassen waehrend des Ausrollens bremst die Bahn und macht die Messung
      // wertlos. In der Leichtathletik heisst das Uebertreten.
      this.phase = 'uebertreten'
      this.onChange()
      return
    }
    if (this.phase === 'anlauf') return
    this.tracker.reset()
    this.resultCm = 0
    this.flightShare = 0
    this.phase = 'anlauf'
    this.onChange()
  }

  private handleTouchEnd = (): void => {
    if (this.phase !== 'anlauf') return
    this.cmAtRelease = this.tracker.forwardCm
    this.phase = 'flug'
    this.onChange()
  }

  private handleSettle = (forwardCm: number): void => {
    if (this.phase !== 'flug') return
    if (forwardCm < MIN_JUMP_CM) {
      // Blosses Antippen ohne Wisch. Kein Fehlversuch, einfach nichts passiert.
      this.phase = 'bereit'
      this.onChange()
      return
    }
    this.resultCm = forwardCm
    this.flightShare = forwardCm > 0 ? (forwardCm - this.cmAtRelease) / forwardCm : 0
    this.phase = 'gemessen'
    this.onChange()
  }

  /** Naechster Versuch. */
  retry(): void {
    this.phase = 'bereit'
    this.resultCm = 0
    this.flightShare = 0
    this.tracker.reset()
    this.onChange()
  }

  toResult(): RunResult {
    return {
      discipline: 'sprung',
      variant: null,
      distanceCm: this.resultCm,
      timeMs: Math.round(this.tracker.elapsedMs),
      samples: this.tracker.getSamples(),
    }
  }
}

// ---------------------------------------------------------------------------
// Sprint
// ---------------------------------------------------------------------------

export type SprintPhase = 'bereit' | 'laeuft' | 'ziel'

export interface SprintSplit {
  /** Anteil der Gesamtstrecke, z. B. 0.25 */
  fraction: number
  label: string
  timeMs: number
}

export class SprintRun {
  phase: SprintPhase = 'bereit'
  targetCm: number
  finishTimeMs = 0
  splits: SprintSplit[] = []

  private tracker: ScrollTracker
  private onChange: () => void
  private startedAt = 0
  private prevCm = 0
  private prevAt = 0
  private pendingSplits: number[]

  constructor(tracker: ScrollTracker, targetCm: number, onChange: () => void) {
    this.tracker = tracker
    this.targetCm = targetCm
    this.onChange = onChange
    this.pendingSplits = [0.25, 0.5, 0.75]
    this.tracker.setEvents({ onProgress: this.handleProgress })
  }

  get progress(): number {
    return Math.min(1, this.tracker.forwardCm / this.targetCm)
  }

  /** Laufende Zeit; nach dem Zieleinlauf die Endzeit. */
  get currentTimeMs(): number {
    if (this.phase === 'bereit') return 0
    if (this.phase === 'ziel') return this.finishTimeMs
    return performance.now() - this.startedAt
  }

  private handleProgress = (forwardCm: number): void => {
    const now = performance.now()

    if (this.phase === 'bereit') {
      // Die Zeitnahme beginnt mit der ersten Bewegung, nicht mit dem
      // Bildschirmaufbau. Sonst bestraft die Uhr das Lesen der Anleitung.
      this.phase = 'laeuft'
      this.startedAt = now
      this.prevCm = 0
      this.prevAt = now
      this.onChange()
    }

    if (this.phase !== 'laeuft') return

    this.captureSplits(forwardCm, now)

    if (forwardCm >= this.targetCm) {
      this.finishTimeMs = this.interpolateCrossing(this.targetCm, forwardCm, now)
      this.phase = 'ziel'
      this.onChange()
      return
    }

    this.prevCm = forwardCm
    this.prevAt = now
  }

  private captureSplits(forwardCm: number, now: number): void {
    while (this.pendingSplits.length > 0) {
      const fraction = this.pendingSplits[0]
      const mark = this.targetCm * fraction
      if (forwardCm < mark) break
      this.pendingSplits.shift()
      this.splits.push({
        fraction,
        label: `${Math.round(fraction * 100)} %`,
        timeMs: this.interpolateCrossing(mark, forwardCm, now),
      })
    }
  }

  /**
   * Der Zieleinlauf faellt fast nie mit einem Scroll-Event zusammen: ein
   * einzelnes Event traegt schnell mehrere Zentimeter. Ohne Interpolation
   * haenge die Zeit an der Bildwiederholrate statt an der Leistung. Darum wird
   * innerhalb des letzten Schrittes linear zurueckgerechnet.
   */
  private interpolateCrossing(markCm: number, currentCm: number, now: number): number {
    const spanCm = currentCm - this.prevCm
    const spanMs = now - this.prevAt
    if (spanCm <= 0 || spanMs <= 0) return now - this.startedAt
    const fraction = (markCm - this.prevCm) / spanCm
    const crossingAt = this.prevAt + fraction * spanMs
    return Math.max(0, crossingAt - this.startedAt)
  }

  reset(): void {
    this.phase = 'bereit'
    this.finishTimeMs = 0
    this.splits = []
    this.pendingSplits = [0.25, 0.5, 0.75]
    this.prevCm = 0
    this.prevAt = 0
    this.tracker.reset()
    this.onChange()
  }

  toResult(variant: string): RunResult {
    return {
      discipline: 'sprint',
      variant,
      distanceCm: this.targetCm,
      timeMs: Math.round(this.finishTimeMs),
      samples: this.tracker.getSamples(),
    }
  }
}

// ---------------------------------------------------------------------------
// Langstrecke
// ---------------------------------------------------------------------------

/**
 * Laenger als diese Pause zaehlt nicht mehr als Laufzeit. Sonst wuerde ein
 * weggelegtes Handy die Durchschnittsgeschwindigkeit ruinieren.
 */
const MAX_GAP_MS = 600

export class LongDistanceRun {
  private tracker: ScrollTracker
  private onChange: () => void
  private activeMs = 0
  private lastProgressAt = 0
  private started = false

  constructor(tracker: ScrollTracker, onChange: () => void) {
    this.tracker = tracker
    this.onChange = onChange
    this.tracker.setEvents({ onProgress: this.handleProgress })
  }

  get distanceCm(): number {
    return this.tracker.forwardCm
  }

  /** Nur die Zeit, in der tatsaechlich gescrollt wurde. */
  get activeTimeMs(): number {
    return this.activeMs
  }

  get averageSpeedCmPerSec(): number {
    if (this.activeMs < 200) return 0
    return (this.distanceCm / this.activeMs) * 1000
  }

  get hasStarted(): boolean {
    return this.started
  }

  private handleProgress = (): void => {
    const now = performance.now()
    if (!this.started) {
      this.started = true
      this.lastProgressAt = now
      this.onChange()
    }
    this.activeMs += Math.min(now - this.lastProgressAt, MAX_GAP_MS)
    this.lastProgressAt = now
  }

  reset(): void {
    this.activeMs = 0
    this.lastProgressAt = 0
    this.started = false
    this.tracker.reset()
    this.onChange()
  }

  toResult(): RunResult {
    return {
      discipline: 'langstrecke',
      variant: null,
      distanceCm: this.distanceCm,
      timeMs: Math.round(this.activeMs),
      samples: this.tracker.getSamples(),
    }
  }
}
